/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../components';
import { Component } from '@angular/core';
import { LanguageService, NotifyService, RbacService, UserService } from '../../services';
import { Router } from '@angular/router';
import { UserModel } from '../../../modules/loadbalancer/shared/models';

@Component({
    selector: 'app-no-access',
    templateUrl: './no-access.component.html',
    styleUrls: ['./no-access.component.scss']
})
export class NoAccessComponent extends BaseComponent {
    /**
     * Page details.
     */
    public pageDetails: any = {
        user: '',
        path: '',
        endpoint: '',
        method: '',
        href: '',
        fullText: ''
    };

    /**
     * Toggle shimmer effect on token container.
     */
    public shimmer: boolean = false;

    constructor(
        private languageService: LanguageService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router,
        private userService: UserService
    ) {
        super();

        const user: UserModel = this.userService.getUser();
        const path: string = this.getPath();
        const route: any = this.lookupPath(path);

        this.pageDetails = this.getFormattedPageDetails(user, path, route);
    }

    /**
     * Notify user when code has been copied successfully.
     */
    public copyCode(): void {
        this.notifyService.success('Copied');

        this.shimmer = true;
        const timerId = setTimeout(() => {
            this.shimmer = false;
            clearTimeout(timerId);
        }, 750);
    }

    /**
     * Get formatted path.
     * @private
     */
    private getPath() {
        const data = this.router.getCurrentNavigation()?.extras?.state?.url;
        const path = data ? data : '';
        this.storePathInLocalStorage(path);

        return path ? path : this.getPathFromLocalStorage();
    }

    /**
     * Store path in local storage.
     * @param path
     * @private
     */
    private storePathInLocalStorage(path: string): void {
        if (path) {
            localStorage.setItem('lb-no-access', path);
        }
    }

    /**
     * Get path from local storage.
     * @private
     */
    private getPathFromLocalStorage(): any {
        return localStorage.getItem('lb-no-access');
    }

    /**
     * Lookup path
     * @param path
     * @private
     */
    private lookupPath(path: string): any {
        const lookup = this.rbacService.getLookupObject();

        return lookup[path];
    }

    /**
     * Get formatted page details.
     * @param user
     * @param path
     * @param route
     * @private
     */
    private getFormattedPageDetails(user: any, path: any, route: { path: any; method: any; }): any {
        const fullText = `
${this.languageService.translate('no_access_message_user')} ${user.email} <br>
${this.languageService.translate('no_access_message_access_path')} ${path}<br>
${this.languageService.translate('no_access_message_required_method')} ${route?.method}.<br>
${this.languageService.translate('no_access_message_endpoint')} ${route?.path}.`;

        return {
            user: user?.email,
            path: path,
            endpoint: route?.path,
            method: route?.method,
            href: `mailto:?subject=RBAC permission request&body=${fullText}`,
            fullText: fullText
        };
    }
}
